import { v2, v3, v4, type DirectionalAction } from '@whaletail/signal-engine';
import type { Btc15PredictorResponse } from './btc15Types';
import { diceCall, rollDie, type DiceFace } from './diceRoll';

export interface ModelPrediction {
  action: DirectionalAction;
  probability: number;
}

export interface DicePrediction extends ModelPrediction {
  /** The literal 1-6 face rolled, kept alongside the derived action/probability so the UI can animate the exact same roll this prediction is tracked under. */
  face: DiceFace;
}

/**
 * The reference baseline every other model is implicitly being compared
 * against: an unweighted coin flip, always 50/50, computed from no signal
 * data whatsoever — deliberately the only "prediction" in this file that
 * takes no `Btc15PredictorResponse` or fitted model. Its purpose is
 * context: if V1-V4's own settled accuracy doesn't clear this by a
 * meaningful margin over enough rounds, their apparent edge may just be
 * sampling noise around 50%, the same noise this baseline's own tracked
 * accuracy will visibly show. `random` is injectable (defaulting to
 * `Math.random`) so this stays testable/deterministic in tests, the same
 * convention `currentRound(nowMs = Date.now())` already uses in
 * `@whaletail/signal-engine` for its own default clock.
 */
export function predictCoinFlip(random: () => number = Math.random): ModelPrediction {
  const action: DirectionalAction = random() < 0.5 ? 'UP' : 'DOWN';
  return { action, probability: 0.5 };
}

/**
 * A second, differently-flavored reference baseline alongside the coin flip:
 * a literal six-sided die, rolled once per round. 4/5/6 calls UP (OVER);
 * 1/2/3 calls DOWN (UNDER) — still an unweighted 50/50, same as the coin
 * flip, just split three-of-six instead of one-of-two. The `face` is kept on
 * the returned object (rather than only exposing `action`/`probability`) so
 * the auto-rolling animation in Btc15DiceRoll and the tracked prediction are
 * always the exact same roll, never two independent random draws.
 */
export function predictDice(random: () => number = Math.random): DicePrediction {
  const face = rollDie(random);
  const action: DirectionalAction = diceCall(face) === 'OVER' ? 'UP' : 'DOWN';
  return { face, action, probability: 0.5 };
}

/**
 * Computes V2's live prediction for the current round, given whatever model
 * the daily scheduled refit has most recently fit (see
 * netlify/functions/btc15-model-refit-scheduled.ts). Shared between the
 * client page (Btc15PredictorPage.tsx) and the server-side scheduled
 * tracker (btc15-tracker-scheduled.ts, which records this into V2's own
 * history) so both compute the exact same number from the exact same
 * formula.
 */
export function predictV2(
  response: Btc15PredictorResponse,
  model: v2.LogisticModel,
): ModelPrediction {
  const features = v2.toFeatureVector({
    signal: response.signal,
    macdHist: response.macdHist,
  });
  const pUp = v2.predictProbability(model, features);
  const action = pUp >= 0.5 ? ('UP' as const) : ('DOWN' as const);
  return { action, probability: action === 'UP' ? pUp : 1 - pUp };
}

/**
 * Computes V3's live prediction for the current round. Uses the real Kalshi
 * settlement threshold when the live quote has one (falling back to the
 * round's own open price otherwise) — the one feature V3 has that V2
 * doesn't, since it can't be backtested but is real and available live. See
 * packages/signal-engine/src/v3/README.md.
 */
export function predictV3(
  response: Btc15PredictorResponse,
  model: v3.GbmModel,
): ModelPrediction {
  const features = v3.toFeatureVector({
    signal: response.signal,
    macdHist: response.macdHist,
    targetPrice: response.quote.targetLine,
  });
  const pUp = v3.predictProbabilityGbm(model, features);
  const action = pUp >= 0.5 ? ('UP' as const) : ('DOWN' as const);
  return { action, probability: action === 'UP' ? pUp : 1 - pUp };
}

/**
 * Computes V4's live prediction: a calibrated convex blend of V1/V2/V3's own
 * P(UP), using whichever V2/V3 models the daily refit most recently fit —
 * the same models predictV2/predictV3 use internally, so V4 always blends
 * exactly what those two functions would independently report for this
 * round, not a separately-computed pair. V1 needs no fitted model; its
 * P(UP) is read straight off the live signal, the same conversion
 * packages/signal-engine/src/v4/oof.ts uses for training data.
 */
export function predictV4(
  response: Btc15PredictorResponse,
  v4Model: v4.V4EnsembleModel,
  v2Model: v2.LogisticModel,
  v3Model: v3.GbmModel,
): ModelPrediction {
  const pUpV1 =
    response.signal.action === 'UP'
      ? response.signal.baseProb
      : 1 - response.signal.baseProb;

  const v2Features = v2.toFeatureVector({
    signal: response.signal,
    macdHist: response.macdHist,
  });
  const pUpV2 = v2.predictProbability(v2Model, v2Features);

  const v3Features = v3.toFeatureVector({
    signal: response.signal,
    macdHist: response.macdHist,
    targetPrice: response.quote.targetLine,
  });
  const pUpV3 = v3.predictProbabilityGbm(v3Model, v3Features);

  const pUp = v4.predictV4Probability(v4Model, { pUpV1, pUpV2, pUpV3 });
  return v4.toDirectionalPrediction(pUp);
}
